import React, { useMemo, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "components/ui/button";

// Token regex: strings (with optional trailing colon → key), numbers, booleans/null.
const TOKEN = /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?)/g;

// Turns a JSON string into an array of coloured React spans without using
// dangerouslySetInnerHTML — every token is rendered as text content.
function highlight(json) {
    const nodes = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = TOKEN.exec(json)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(json.slice(lastIndex, match.index));
        }
        const [full, str, colon, keyword, number] = match;
        if (str !== undefined) {
            if (colon !== undefined) {
                nodes.push(
                    <span key={key++} className="text-primary">
                        {str}
                    </span>
                );
                nodes.push(colon);
            } else {
                nodes.push(
                    <span key={key++} className="text-success">
                        {str}
                    </span>
                );
            }
        } else if (keyword !== undefined) {
            nodes.push(
                <span key={key++} className="text-destructive">
                    {keyword}
                </span>
            );
        } else if (number !== undefined) {
            nodes.push(
                <span key={key++} className="text-warning">
                    {number}
                </span>
            );
        } else {
            nodes.push(full);
        }
        lastIndex = match.index + full.length;
    }
    if (lastIndex < json.length) {
        nodes.push(json.slice(lastIndex));
    }
    return nodes;
}

function RawView({ raw, fileName = "live_event_status.json" }) {
    const [copied, setCopied] = useState(false);
    const jsonString = useMemo(() => JSON.stringify(raw ?? {}, null, 2), [raw]);
    const nodes = useMemo(() => highlight(jsonString), [jsonString]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(jsonString);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (error) {
            // Clipboard can be unavailable (insecure context); fail quietly.
        }
    };

    return (
        <div className="mt-4 overflow-hidden rounded-lg border border-border bg-card">
            <div className="flex items-center gap-2.5 border-b border-border px-3.5 py-2.5 text-sm text-muted-foreground">
                <span className="font-mono">{fileName}</span>
                <Button size="sm" variant="outline" className="ml-auto" onClick={copy}>
                    {copied ? <Check /> : <Copy />}
                    {copied ? "Copied" : "Copy"}
                </Button>
            </div>
            <pre className="m-0 overflow-x-auto p-4 font-mono text-[12.5px] leading-relaxed">
                {nodes}
            </pre>
        </div>
    );
}

export default RawView;
